import * as THREE from 'three';
import type { WorkerState } from '@/types';
import { ACTIONS, resolveTargetPosition, resolveTargetRotation } from '@/actions/action-registry';
import { createCoffeeCupProp, createBookProp } from './worker-props';
import {
  SKIN_COLOR, BODY_COLOR, LEGS_COLOR,
  BREATHING_SPEED, BREATHING_AMOUNT,
  TYPING_BOB_SPEED, TYPING_BOB_AMOUNT,
  WRITING_BOB_SPEED, WRITING_BOB_AMOUNT,
  THINKING_HEAD_SPEED, THINKING_HEAD_AMOUNT,
  THINKING_SWAY_SPEED, THINKING_SWAY_AMOUNT,
  WORKER_NAMES, DESK_POSITIONS,
  WALK_SPEED, DESK_HALF_W, DESK_HALF_D,
  CENTER_LANE_Z, LEFT_LANE_X, RIGHT_LANE_X,
} from '@/utils/constants';

/**
 * Primitive-based character with interactive arms, name label,
 * and per-worker whiteboard offset to prevent stacking.
 */
export class Worker {
  readonly id: string;
  readonly deskIndex: number;
  readonly group: THREE.Group;
  readonly displayName: string;

  private state: WorkerState = 'IDLE';
  private previousState: WorkerState = 'IDLE';
  private currentAction = 'idle';
  private previousAction = 'idle';
  private elapsedTime = 0;
  private isThinking = false;
  private isTalking = false;
  private mouthMesh: THREE.Mesh | null = null;

  // Active indicator (green pulsing ring at feet)
  private activeRing: THREE.Mesh | null = null;
  private activeRingMat: THREE.MeshStandardMaterial | null = null;

  // Walk (waypoint-based)
  private waypoints: THREE.Vector3[] = [];
  private waypointIndex = 0;
  private segStartPos = new THREE.Vector3();
  private segEndPos = new THREE.Vector3();
  private segTimer = 0;
  private segDuration = 0;
  private walkFinalRot = 0;
  private isWalking = false;
  private pendingAction = '';
  private _arrivalState: WorkerState | undefined;
  private onWalkComplete: (() => void) | undefined;

  // Body parts for animation
  private bodyMesh: THREE.Mesh;
  private headGroup: THREE.Group;
  private leftArm: THREE.Mesh;
  private rightArm: THREE.Mesh;
  private labelSprite: THREE.Sprite;

  // Props (held objects — hidden by default)
  private coffeeProp: THREE.Group;
  private bookProp: THREE.Group;

  // Arm rest positions / rotations
  private armRestZ = 0.15;
  private armRestRotZ_L = 0.15;
  private armRestRotZ_R = -0.15;

  constructor(id: string, deskIndex: number, position: THREE.Vector3) {
    this.id = id;
    this.deskIndex = deskIndex;
    this.displayName = WORKER_NAMES[deskIndex] || `Worker ${deskIndex + 1}`;
    this.group = new THREE.Group();
    this.group.name = id;
    this.group.userData = { id, type: 'worker', deskIndex, interactive: true, action: 'select-worker' };
    this.group.position.copy(position);

    const skinMat = new THREE.MeshStandardMaterial({ color: SKIN_COLOR });
    const bodyMat = new THREE.MeshStandardMaterial({ color: BODY_COLOR });
    const legsMat = new THREE.MeshStandardMaterial({ color: LEGS_COLOR });

    // Legs
    const legGeo = new THREE.CylinderGeometry(0.05, 0.05, 0.5, 8);
    const leftLeg = new THREE.Mesh(legGeo, legsMat);
    leftLeg.position.set(-0.08, 0.25, 0);
    leftLeg.castShadow = true;
    this.group.add(leftLeg);
    const rightLeg = new THREE.Mesh(legGeo, legsMat);
    rightLeg.position.set(0.08, 0.25, 0);
    rightLeg.castShadow = true;
    this.group.add(rightLeg);

    // Body
    const bodyGeo = new THREE.CapsuleGeometry(0.125, 0.35, 4, 8);
    this.bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
    this.bodyMesh.position.y = 0.75;
    this.bodyMesh.castShadow = true;
    this.group.add(this.bodyMesh);

    // Arms — positioned to animate
    const armGeo = new THREE.CylinderGeometry(0.04, 0.035, 0.35, 6);

    this.leftArm = new THREE.Mesh(armGeo, skinMat);
    this.leftArm.position.set(-0.2, 0.7, 0);
    this.leftArm.rotation.z = this.armRestRotZ_L;
    this.leftArm.castShadow = true;
    this.group.add(this.leftArm);

    this.rightArm = new THREE.Mesh(armGeo, skinMat);
    this.rightArm.position.set(0.2, 0.7, 0);
    this.rightArm.rotation.z = this.armRestRotZ_R;
    this.rightArm.castShadow = true;
    this.group.add(this.rightArm);

    // Head
    this.headGroup = new THREE.Group();
    this.headGroup.position.y = 1.15;
    this.group.add(this.headGroup);

    const headGeo = new THREE.SphereGeometry(0.2, 16, 16);
    const head = new THREE.Mesh(headGeo, skinMat);
    head.castShadow = true;
    this.headGroup.add(head);

    // Eyes
    const eyeMat = new THREE.MeshStandardMaterial({ color: 0x222222 });
    const eyeGeo = new THREE.SphereGeometry(0.03, 8, 8);
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
    leftEye.position.set(-0.07, 0.04, 0.17);
    this.headGroup.add(leftEye);
    const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
    rightEye.position.set(0.07, 0.04, 0.17);
    this.headGroup.add(rightEye);

    // Mouth (rounded box — scales Y to animate talking)
    const mouthGeo = new THREE.BoxGeometry(0.08, 0.04, 0.03);
    const mouthMat = new THREE.MeshStandardMaterial({ color: 0x553333 });
    this.mouthMesh = new THREE.Mesh(mouthGeo, mouthMat);
    this.mouthMesh.position.set(0, -0.06, 0.18);
    // Default: small closed mouth (acts as a resting smile)
    this.mouthMesh.scale.set(1, 0.5, 1);
    this.headGroup.add(this.mouthMesh);

    // ── Name Label (sprite) ────────────────────────────────
    this.labelSprite = this.createLabel(this.displayName);
    this.labelSprite.position.y = 1.55;
    this.group.add(this.labelSprite);

    // ── Props (held objects) ─────────────────────────────
    this.coffeeProp = createCoffeeCupProp();
    this.coffeeProp.position.set(0.2, 0.55, 0.08); // Near right hand
    this.group.add(this.coffeeProp);

    this.bookProp = createBookProp();
    this.bookProp.position.set(0.2, 0.55, 0.08); // Near right hand
    this.group.add(this.bookProp);

    // ── Enable Layer 1 for raycaster interactivity ───────
    this.group.traverse((child) => {
      child.layers.enable(1);
    });
  }

  private createLabel(text: string): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    // Background pill
    ctx.fillStyle = 'rgba(20, 20, 40, 0.75)';
    const rx = 8, w = 256, h = 48, y = 8;
    ctx.beginPath();
    ctx.roundRect(0, y, w, h, rx);
    ctx.fill();

    // Text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 28px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, w / 2, y + h / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.8, 0.2, 1);
    return sprite;
  }

  getState(): WorkerState { return this.state; }
  getCurrentAction(): string { return this.currentAction; }
  get isCurrentlyWalking(): boolean { return this.isWalking; }

  /**
   * Show or hide a pulsing green ring at the worker's feet.
   * Indicates this worker is actively executing a real AI task.
   */
  setActiveIndicator(active: boolean): void {
    if (active && !this.activeRing) {
      const geo = new THREE.RingGeometry(0.22, 0.28, 32);
      this.activeRingMat = new THREE.MeshStandardMaterial({
        color: 0x22c55e,
        emissive: 0x22c55e,
        emissiveIntensity: 0.5,
        transparent: true,
        opacity: 0.6,
        side: THREE.DoubleSide,
      });
      this.activeRing = new THREE.Mesh(geo, this.activeRingMat);
      this.activeRing.rotation.x = -Math.PI / 2; // Lay flat on ground
      this.activeRing.position.y = 0.02; // Just above floor
      this.group.add(this.activeRing);
    } else if (!active && this.activeRing) {
      this.group.remove(this.activeRing);
      this.activeRing.geometry.dispose();
      this.activeRingMat?.dispose();
      this.activeRing = null;
      this.activeRingMat = null;
    }
  }

  /**
   * Toggle a subtle emissive glow on the worker's body for hover highlight.
   */
  setHighlight(on: boolean): void {
    const mat = this.bodyMesh.material as THREE.MeshStandardMaterial;
    if (on) {
      mat.emissive.setHex(0x4466ff);
      mat.emissiveIntensity = 0.15;
    } else {
      mat.emissive.setHex(0x000000);
      mat.emissiveIntensity = 0;
    }
  }

  /**
   * Walk to an arbitrary world position with a target rotation.
   * Uses waypoint routing to avoid desks.
   */
  walkToPosition(
    target: THREE.Vector3,
    rotation: number,
    arrivalState: WorkerState = 'IDLE',
    onComplete?: () => void,
  ): void {
    const start = this.group.position.clone();
    this.waypoints = computeWaypoints(start, target);
    this.waypointIndex = 0;
    this.walkFinalRot = rotation;
    this.isWalking = true;
    this.pendingAction = 'idle';
    this._arrivalState = arrivalState;
    this.onWalkComplete = onComplete;
    // Hide props during walking
    this.coffeeProp.visible = false;
    this.bookProp.visible = false;
    this.startNextSegment();
  }

  /**
   * Enter or exit THINKING state.
   * Saves and restores previous state so worker resumes what they were doing.
   */
  setThinking(thinking: boolean): void {
    if (thinking && !this.isThinking) {
      this.isThinking = true;
      this.previousState = this.state;
      this.previousAction = this.currentAction;
      this.state = 'THINKING';
    } else if (!thinking && this.isThinking) {
      this.isThinking = false;
      this.state = this.previousState;
      this.currentAction = this.previousAction;
    }
  }

  /**
   * Toggle talking animation (mouth opens/closes).
   */
  setTalking(talking: boolean): void {
    this.isTalking = talking;
    if (!talking && this.mouthMesh) {
      // Reset to resting mouth
      this.mouthMesh.scale.set(1, 0.5, 1);
    }
  }

  /**
   * Command this worker to perform an action.
   * Whiteboard positions are offset per-worker to prevent stacking.
   */
  performAction(actionId: string): void {
    if (actionId === this.currentAction && !this.isWalking) return;

    const action = ACTIONS[actionId];
    if (!action) return;

    this.pendingAction = actionId;

    // Resolve target — with per-worker offset at whiteboard
    const targetPos = resolveTargetPosition(action, this.deskIndex);

    // Offset workers at whiteboard so they don't overlap
    if (action.target === 'whiteboard') {
      targetPos.z += this.deskIndex * 0.6; // Spread along Z axis
    }

    const targetRot = resolveTargetRotation(action, this.deskIndex);

    const start = this.group.position.clone();
    this.waypoints = computeWaypoints(start, targetPos);
    this.waypointIndex = 0;
    this.walkFinalRot = targetRot;
    this.isWalking = true;
    this.startNextSegment();

    if (actionId === 'type') this.state = 'WALKING_TO_DESK';
    else if (actionId === 'write') this.state = 'WALKING_TO_WHITEBOARD';
    else this.state = 'IDLE';
  }

  update(delta: number): void {
    this.elapsedTime += delta;

    // Pulse the green active ring (subtle glow only, no scale bounce)
    if (this.activeRing && this.activeRingMat) {
      this.activeRingMat.emissiveIntensity = 0.3 + Math.sin(this.elapsedTime * 2) * 0.2;
    }

    // Animate mouth when talking
    if (this.isTalking && this.mouthMesh) {
      const openAmount = 0.5 + Math.abs(Math.sin(this.elapsedTime * 8)) * 1.5;
      this.mouthMesh.scale.set(1, openAmount, 1);
    }

    if (this.isWalking) {
      this.updateWalk(delta);
      return;
    }

    switch (this.state) {
      case 'IDLE': this.animateIdle(); break;
      case 'TYPING': this.animateTyping(); break;
      case 'WRITING': this.animateWriting(); break;
      case 'THINKING': this.animateThinking(); break;
      case 'STRETCHING': this.animateStretching(); break;
      case 'DRINKING_COFFEE': this.animateDrinkingCoffee(); break;
      case 'BROWSING_BOOKSHELF': this.animateBrowsingBookshelf(); break;
      case 'COLLABORATING': this.animateCollaborating(); break;
      case 'CELEBRATING': this.animateCelebrating(); break;
    }
  }

  /** Start walking to the next waypoint in the queue. */
  private startNextSegment(): void {
    if (this.waypointIndex >= this.waypoints.length) return;
    this.segStartPos.copy(this.group.position);
    this.segEndPos.copy(this.waypoints[this.waypointIndex]);
    const dist = this.segStartPos.distanceTo(this.segEndPos);
    this.segDuration = Math.max(dist / WALK_SPEED, 0.15); // min 150ms to avoid pop
    this.segTimer = 0;
  }

  private updateWalk(delta: number): void {
    this.segTimer += delta;
    const t = Math.min(this.segTimer / this.segDuration, 1);
    const eased = t * t * (3 - 2 * t); // smoothstep

    // Lerp position along current segment
    this.group.position.lerpVectors(this.segStartPos, this.segEndPos, eased);

    // Face the direction of travel (intermediate segments) or final rotation (last segment)
    const isLastSegment = this.waypointIndex >= this.waypoints.length - 1;
    let targetRot: number;
    if (isLastSegment) {
      // Blend toward final rotation during the last segment
      const dir = new THREE.Vector3().subVectors(this.segEndPos, this.segStartPos);
      const walkRot = Math.atan2(dir.x, dir.z);
      // Blend from walkRot to final rotation over the last segment
      targetRot = isLastSegment && t > 0.5
        ? lerpAngle(walkRot, this.walkFinalRot, (t - 0.5) * 2)
        : walkRot;
    } else {
      // Face direction of travel
      const dir = new THREE.Vector3().subVectors(this.segEndPos, this.segStartPos);
      targetRot = Math.atan2(dir.x, dir.z);
    }
    this.group.rotation.y = lerpAngle(this.group.rotation.y, targetRot, Math.min(delta * 8, 1));

    // Walking bob
    this.group.position.y = Math.abs(Math.sin(this.segTimer * 8)) * 0.03;

    // Arm swing while walking
    const swing = Math.sin(this.segTimer * 8) * 0.4;
    this.leftArm.rotation.x = swing;
    this.rightArm.rotation.x = -swing;
    this.leftArm.rotation.z = this.armRestRotZ_L;
    this.rightArm.rotation.z = this.armRestRotZ_R;

    if (t >= 1) {
      this.waypointIndex++;
      if (this.waypointIndex < this.waypoints.length) {
        // More waypoints — start next segment
        this.startNextSegment();
        return;
      }

      // All waypoints reached — finalize walk
      this.isWalking = false;
      this.group.position.y = 0;
      this.group.rotation.y = this.walkFinalRot;
      this.currentAction = this.pendingAction;
      this.leftArm.rotation.x = 0;
      this.rightArm.rotation.x = 0;

      if (this._arrivalState) {
        this.state = this._arrivalState;
        this._arrivalState = undefined;
      } else if (this.pendingAction === 'type') {
        this.state = 'TYPING';
      } else if (this.pendingAction === 'write') {
        this.state = 'WRITING';
      } else {
        this.state = 'IDLE';
      }

      // Update prop visibility based on new state
      this.updatePropVisibility();

      if (this.onWalkComplete) {
        const cb = this.onWalkComplete;
        this.onWalkComplete = undefined;
        cb();
      }
    }
  }

  private animateIdle(): void {
    this.group.position.y = Math.sin(this.elapsedTime * BREATHING_SPEED) * BREATHING_AMOUNT;

    // Arms at rest, slight sway
    this.leftArm.rotation.x = Math.sin(this.elapsedTime * 0.5) * 0.03;
    this.rightArm.rotation.x = Math.sin(this.elapsedTime * 0.5 + 0.5) * 0.03;
    this.leftArm.rotation.z = this.armRestRotZ_L;
    this.rightArm.rotation.z = this.armRestRotZ_R;
  }

  private animateTyping(): void {
    const bobZ = Math.sin(this.elapsedTime * TYPING_BOB_SPEED) * TYPING_BOB_AMOUNT;
    this.bodyMesh.position.z = bobZ;
    this.headGroup.position.z = bobZ * 0.5;
    this.headGroup.rotation.x = 0.1;

    // Arms forward and alternating — typing on keyboard
    this.leftArm.rotation.z = -0.2;  // Arms angled inward
    this.rightArm.rotation.z = 0.2;
    this.leftArm.rotation.x = -0.6 + Math.sin(this.elapsedTime * 8) * 0.15;
    this.rightArm.rotation.x = -0.6 + Math.sin(this.elapsedTime * 8 + Math.PI) * 0.15;
    // Move arms forward toward desk
    this.leftArm.position.z = 0.1;
    this.rightArm.position.z = 0.1;
  }

  private animateWriting(): void {
    const bobY = Math.sin(this.elapsedTime * WRITING_BOB_SPEED) * WRITING_BOB_AMOUNT;
    this.group.position.y = bobY;

    this.headGroup.rotation.x = -0.05;
    this.headGroup.rotation.y = Math.sin(this.elapsedTime * 0.5) * 0.1;

    // Right arm raised and writing — moves up and down
    this.rightArm.rotation.z = -0.8;
    this.rightArm.rotation.x = -0.3 + Math.sin(this.elapsedTime * 3) * 0.2;
    this.rightArm.position.z = 0;

    // Left arm relaxed at side
    this.leftArm.rotation.z = this.armRestRotZ_L;
    this.leftArm.rotation.x = 0;
    this.leftArm.position.z = 0;
  }

  private animateThinking(): void {
    // Gentle body sway
    this.group.position.y = Math.sin(this.elapsedTime * THINKING_SWAY_SPEED) * THINKING_SWAY_AMOUNT;

    // Head tilted up-right, slow rotation as if pondering
    this.headGroup.rotation.x = -0.1; // tilted slightly up
    this.headGroup.rotation.y = Math.sin(this.elapsedTime * THINKING_HEAD_SPEED) * THINKING_HEAD_AMOUNT;
    this.headGroup.rotation.z = 0.08; // tilted to the right

    // Right hand raised near chin (thinking pose)
    this.rightArm.rotation.z = -0.5;
    this.rightArm.rotation.x = -0.8 + Math.sin(this.elapsedTime * 0.8) * 0.05;
    this.rightArm.position.z = 0.08;

    // Left arm relaxed at side, slight sway
    this.leftArm.rotation.z = this.armRestRotZ_L;
    this.leftArm.rotation.x = Math.sin(this.elapsedTime * 0.4) * 0.03;
    this.leftArm.position.z = 0;
  }

  // ── Ambient Animation States ──────────────────────────────

  private animateStretching(): void {
    // Arms reach up, body extends, slight lean side to side
    this.leftArm.rotation.z = -0.8 + Math.sin(this.elapsedTime * 1.5) * 0.15;
    this.rightArm.rotation.z = 0.8 - Math.sin(this.elapsedTime * 1.5) * 0.15;
    this.leftArm.rotation.x = -Math.PI * 0.4;
    this.rightArm.rotation.x = -Math.PI * 0.4;
    this.leftArm.position.z = 0;
    this.rightArm.position.z = 0;
    this.bodyMesh.position.y = 0.75 + Math.sin(this.elapsedTime * 1.2) * 0.03;
    this.headGroup.rotation.x = -0.15; // Look up
    this.headGroup.rotation.y = 0;
    this.headGroup.rotation.z = 0;
    this.group.position.y = 0;
  }

  private animateDrinkingCoffee(): void {
    // Right arm raised holding cup, occasional sip motion
    this.rightArm.rotation.z = -0.5;
    this.rightArm.rotation.x = -0.7 + Math.sin(this.elapsedTime * 0.8) * 0.15;
    this.rightArm.position.z = 0.08;
    this.leftArm.rotation.x = Math.sin(this.elapsedTime * 0.3) * 0.04;
    this.leftArm.rotation.z = this.armRestRotZ_L;
    this.leftArm.position.z = 0;
    this.headGroup.rotation.x = Math.sin(this.elapsedTime * 0.8) * 0.05 - 0.05;
    this.headGroup.rotation.y = 0;
    this.headGroup.rotation.z = 0;
    this.group.position.y = Math.sin(this.elapsedTime * BREATHING_SPEED) * BREATHING_AMOUNT;
  }

  private animateBrowsingBookshelf(): void {
    // Head turns left/right scanning spines, one arm extended
    this.headGroup.rotation.y = Math.sin(this.elapsedTime * 0.6) * 0.2;
    this.headGroup.rotation.x = 0;
    this.headGroup.rotation.z = 0;
    this.rightArm.rotation.x = -0.5 + Math.sin(this.elapsedTime * 0.4) * 0.1;
    this.rightArm.rotation.z = -0.2;
    this.rightArm.position.z = 0.05;
    this.leftArm.rotation.x = 0;
    this.leftArm.rotation.z = this.armRestRotZ_L;
    this.leftArm.position.z = 0;
    this.group.position.y = Math.sin(this.elapsedTime * BREATHING_SPEED) * BREATHING_AMOUNT;
  }

  private animateCollaborating(): void {
    // Body sway, arm gestures, head nods — looks like a conversation
    this.group.position.y = Math.sin(this.elapsedTime * 0.8) * 0.01;
    this.rightArm.rotation.x = -0.3 + Math.sin(this.elapsedTime * 1.5) * 0.2;
    this.rightArm.rotation.z = -0.3;
    this.rightArm.position.z = 0;
    this.leftArm.rotation.x = Math.sin(this.elapsedTime * 0.5) * 0.05;
    this.leftArm.rotation.z = this.armRestRotZ_L;
    this.leftArm.position.z = 0;
    this.headGroup.rotation.y = Math.sin(this.elapsedTime * 0.4) * 0.1;
    this.headGroup.rotation.x = Math.sin(this.elapsedTime * 1.2) * 0.05; // Nodding
    this.headGroup.rotation.z = 0;
  }

  private animateCelebrating(): void {
    // Both arms raised high, slight bounce/jump
    this.leftArm.rotation.z = -1.0;
    this.rightArm.rotation.z = 1.0;
    this.leftArm.rotation.x = -Math.PI * 0.3;
    this.rightArm.rotation.x = -Math.PI * 0.3;
    this.leftArm.position.z = 0;
    this.rightArm.position.z = 0;
    this.group.position.y = Math.abs(Math.sin(this.elapsedTime * 6)) * 0.04;
    this.headGroup.rotation.x = -0.1; // Look up slightly
    this.headGroup.rotation.y = 0;
    this.headGroup.rotation.z = 0;
  }

  /**
   * Enter or exit CELEBRATING state.
   * Saves and restores previous state (like setThinking).
   */
  private _isCelebrating = false;
  private _preCelebrateState: WorkerState = 'IDLE';
  private _preCelebrateAction = 'idle';

  setCelebrating(celebrating: boolean): void {
    if (celebrating && !this._isCelebrating) {
      this._isCelebrating = true;
      this._preCelebrateState = this.state;
      this._preCelebrateAction = this.currentAction;
      this.state = 'CELEBRATING';
    } else if (!celebrating && this._isCelebrating) {
      this._isCelebrating = false;
      this.state = this._preCelebrateState;
      this.currentAction = this._preCelebrateAction;
    }
  }

  /**
   * Directly set the worker's state (used by ambient engine).
   * Also toggles prop visibility to match the state.
   */
  setState(state: WorkerState): void {
    this.state = state;
    this.updatePropVisibility();
  }

  /** Show/hide props based on current state */
  private updatePropVisibility(): void {
    this.coffeeProp.visible = this.state === 'DRINKING_COFFEE';
    this.bookProp.visible = this.state === 'BROWSING_BOOKSHELF';
  }

  /**
   * Temporarily hide the active ring (used during ambient behaviors).
   */
  setActiveRingVisible(visible: boolean): void {
    if (this.activeRing) {
      this.activeRing.visible = visible;
    }
  }

  dispose(): void {
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Sprite) {
        (child.material as THREE.SpriteMaterial).map?.dispose();
        child.material.dispose();
      }
    });
  }
}

// ── Waypoint Helpers (module-level) ──────────────────────────

/** Lerp between two angles (shortest path). */
function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * t;
}

/**
 * Check if a line segment from A to B passes through a desk's AABB (XZ plane).
 * Uses a simplified AABB–segment intersection test.
 */
function segmentIntersectsDesk(
  ax: number, az: number, bx: number, bz: number,
  cx: number, cz: number,  // desk center
): boolean {
  // Expand desk AABB
  const minX = cx - DESK_HALF_W;
  const maxX = cx + DESK_HALF_W;
  const minZ = cz - DESK_HALF_D;
  const maxZ = cz + DESK_HALF_D;

  // Quick check: if both endpoints are on the same side of any edge, no intersection
  if (ax < minX && bx < minX) return false;
  if (ax > maxX && bx > maxX) return false;
  if (az < minZ && bz < minZ) return false;
  if (az > maxZ && bz > maxZ) return false;

  // Check if either point is inside the box
  if (ax >= minX && ax <= maxX && az >= minZ && az <= maxZ) return true;
  if (bx >= minX && bx <= maxX && bz >= minZ && bz <= maxZ) return true;

  // Parametric line-box intersection for the 4 edges
  const dx = bx - ax;
  const dz = bz - az;

  const edges = [
    { isX: true, val: minX }, { isX: true, val: maxX },
    { isX: false, val: minZ }, { isX: false, val: maxZ },
  ];

  for (const edge of edges) {
    const denom = edge.isX ? dx : dz;
    if (Math.abs(denom) < 1e-6) continue;
    const t = ((edge.isX ? edge.val - ax : edge.val - az)) / denom;
    if (t < 0 || t > 1) continue;
    const ix = ax + dx * t;
    const iz = az + dz * t;
    if (ix >= minX - 0.001 && ix <= maxX + 0.001 &&
        iz >= minZ - 0.001 && iz <= maxZ + 0.001) {
      return true;
    }
  }

  return false;
}

/** Check if a segment from (ax,az)→(bx,bz) crosses ANY desk AABB */
function anyDeskBlocks(ax: number, az: number, bx: number, bz: number): boolean {
  for (const desk of DESK_POSITIONS) {
    if (segmentIntersectsDesk(ax, az, bx, bz, desk.position.x, desk.position.z)) {
      return true;
    }
  }
  return false;
}

/** Return the desk whose AABB contains (x, z), or null */
function getDeskContaining(x: number, z: number): { position: THREE.Vector3 } | null {
  for (const desk of DESK_POSITIONS) {
    if (Math.abs(x - desk.position.x) < DESK_HALF_W &&
        Math.abs(z - desk.position.z) < DESK_HALF_D) {
      return desk;
    }
  }
  return null;
}

/**
 * Try to route from start to end through a lane (horizontal or vertical).
 * axis='z' means the lane runs at a fixed z (like CENTER_LANE_Z).
 * axis='x' means the lane runs at a fixed x (like LEFT_LANE_X or RIGHT_LANE_X).
 * Returns waypoints array if successful, or null if any sub-segment is blocked.
 */
function tryRouteThroughLane(
  start: THREE.Vector3, end: THREE.Vector3,
  laneValue: number, axis: 'x' | 'z',
): THREE.Vector3[] | null {
  const waypoints: THREE.Vector3[] = [];

  // Step 1: Move from start to lane entry
  const laneEntry = axis === 'z'
    ? new THREE.Vector3(start.x, 0, laneValue)
    : new THREE.Vector3(laneValue, 0, start.z);

  if (anyDeskBlocks(start.x, start.z, laneEntry.x, laneEntry.z)) {
    return null; // Can't reach lane
  }
  waypoints.push(laneEntry);

  // Step 2: Move along lane to align with destination
  const laneExit = axis === 'z'
    ? new THREE.Vector3(end.x, 0, laneValue)
    : new THREE.Vector3(laneValue, 0, end.z);

  if (anyDeskBlocks(laneEntry.x, laneEntry.z, laneExit.x, laneExit.z)) {
    return null; // Lane itself is blocked
  }
  // Only add laneExit if it's significantly different from laneEntry
  if (Math.abs(laneEntry.x - laneExit.x) > 0.3 || Math.abs(laneEntry.z - laneExit.z) > 0.3) {
    waypoints.push(laneExit);
  }

  // Step 3: Move from lane exit to destination
  if (anyDeskBlocks(laneExit.x, laneExit.z, end.x, end.z)) {
    return null; // Can't reach destination from lane
  }
  waypoints.push(end.clone());

  return waypoints;
}

/**
 * Compute waypoints from start to end, routing around desks via multi-lane system.
 *
 * Algorithm:
 *  1. Direct path clear → use it
 *  2. If starting inside a desk AABB, step out to center lane first
 *  3. Try center lane (z = CENTER_LANE_Z) between desk rows
 *  4. Try left lane (x = LEFT_LANE_X) along left wall
 *  5. Try right lane (x = RIGHT_LANE_X) along right wall
 *  6. Fallback: L-shaped route via room center
 */
function computeWaypoints(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3[] {
  // 1. Direct path clear?
  if (!anyDeskBlocks(start.x, start.z, end.x, end.z)) {
    return [end.clone()];
  }

  // 2. If starting inside a desk AABB, step out to center lane first
  let effectiveStart = start;
  const startDesk = getDeskContaining(start.x, start.z);
  if (startDesk) {
    // Step toward center lane on Z axis
    effectiveStart = new THREE.Vector3(start.x, 0, CENTER_LANE_Z);
  }

  // Also check if end is inside a desk AABB — if so, we still need to reach it
  // (worker walking TO their desk position is valid)

  // 3. Try center lane (z = CENTER_LANE_Z)
  const centerPath = tryRouteThroughLane(effectiveStart, end, CENTER_LANE_Z, 'z');
  if (centerPath) {
    return startDesk
      ? [new THREE.Vector3(start.x, 0, CENTER_LANE_Z), ...centerPath]
      : centerPath;
  }

  // 4. Try left lane (x = LEFT_LANE_X)
  const leftPath = tryRouteThroughLane(effectiveStart, end, LEFT_LANE_X, 'x');
  if (leftPath) {
    return startDesk
      ? [new THREE.Vector3(start.x, 0, CENTER_LANE_Z), ...leftPath]
      : leftPath;
  }

  // 5. Try right lane (x = RIGHT_LANE_X)
  const rightPath = tryRouteThroughLane(effectiveStart, end, RIGHT_LANE_X, 'x');
  if (rightPath) {
    return startDesk
      ? [new THREE.Vector3(start.x, 0, CENTER_LANE_Z), ...rightPath]
      : rightPath;
  }

  // 6. Fallback: L-shaped route through room center
  const fallback: THREE.Vector3[] = [];
  if (startDesk) {
    fallback.push(new THREE.Vector3(start.x, 0, CENTER_LANE_Z));
  }
  fallback.push(new THREE.Vector3(0, 0, CENTER_LANE_Z));
  fallback.push(new THREE.Vector3(end.x, 0, CENTER_LANE_Z));
  fallback.push(end.clone());
  return fallback;
}
